/* eslint-disable jsx-a11y/no-static-element-interactions */
import { makeStyles } from '@mui/styles';
import IconButton from 'apollo-react/components/IconButton';
import Tooltip from 'apollo-react/components/Tooltip';
import ArrowDown from 'apollo-react-icons/ArrowDown';
import ArrowUp from 'apollo-react-icons/ArrowUp';
import Drag from 'apollo-react-icons/Drag';
import Plus from 'apollo-react-icons/Plus';
import Trash from 'apollo-react-icons/Trash';
import classNames from 'classnames';
import React from 'react';
import { useDispatch } from 'react-redux';

import { useFormSelector } from '../../../../custom-hooks';
import { useTranslation } from '../../../../custom-hooks/useTranslation';
import { getUITA } from '../../../../utils/commonUtils';
import {
  addNewColumnToFormModel,
  addNewSingleTabToFormModel,
  addNewSubGridToFormModel,
  changeElementPositionFormModel,
  deleteElementFromFormModel,
} from '../../../actions/editor.actions';
import { MOVE_BUTTON_TYPES } from '../../../constants/editor.constants';
import { SS_CONTROL_TYPE } from '../../../constants/form-manager.constants';
import {
  getChildNodes,
  getIsAddButtonDisabled,
  getIsDeleteButtonDisabled,
} from '../../../selectors/editor.selectors';
import { getAddButtonTooltipText } from '../../../utils/editor.utils';
import { equalityFn } from '../../../utils/form-manager.utils';

const getRotate = (component) =>
  component.controlType === SS_CONTROL_TYPE.GRID_COLUMN ||
  component.parent.includes(SS_CONTROL_TYPE.ADVANCED_TEXT_BOX) ||
  component.controlType === SS_CONTROL_TYPE.SINGLE_TAB;

const useStyles = makeStyles({
  root: { display: 'flex', alignItems: 'center', height: '100%' },
  element: { flexGrow: 1, width: '100%', height: '100%' },
  elementSelected: { flexGrow: 1, width: 'calc(100% - 160px)' },
  dragIcon: {
    cursor: ({ canDragControl }) =>
      canDragControl() ? 'move !important' : 'not-allowed !important',
  },
  rotate: { transform: 'rotate(-90deg)' },
  floatingButtons: {
    position: 'absolute',
    display: 'flex',
    flexGrow: 'nowrap',
    backgroundColor: 'white',
    borderRadius: 6,
    zIndex: 1001,
    boxShadow: '0px 1px 10px 0 #d9d9d9',
    right: 0,
    bottom: 'calc(100% + 4px)',
  },
  floatingButtonsBottom: {
    top: 'calc(100% + 4px)',
    backgroundColor: 'white',
    position: 'absolute',
    display: 'flex',
    flexGrow: 'nowrap',
    borderRadius: 6,
    zIndex: 1001,
    boxShadow: '0px 1px 10px 0 #d9d9d9',
    right: 0,
  },
  disabled: {
    opacity: 0.4,
  },
  floatingButtonsIcon: {
    right: -40,
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const NewComponentOptions = ({
  children,
  isSelected,
  hasControlWrapper,
  component,
  setDragIconPressed,
  canDragControl,
  showTextBoxOptions,
}) => {
  const classes = useStyles({ canDragControl });
  const translateString = useTranslation();
  const dispatch = useDispatch();
  const { controlId, parent } = component;
  const childrenNodes = useFormSelector(getChildNodes, { id: parent }, equalityFn);
  const isAddBtnDisabled = useFormSelector(getIsAddButtonDisabled, {
    id: controlId,
  });
  const isDeleteBtnDisabled = useFormSelector(getIsDeleteButtonDisabled, {
    id: controlId,
  });

  const isMoveBtnDisabled = (component, moveType) => {
    if (component.parent === 'root' || component?.controlType === SS_CONTROL_TYPE.SUB_GRID) {
      return true;
    }
    const currentIndex = childrenNodes.indexOf(component.controlId);
    const isDisabled =
      childrenNodes.length === 0 ||
      component.parent.includes(SS_CONTROL_TYPE.ADVANCED_TEXT_BOX) ||
      (currentIndex === 0 && moveType === MOVE_BUTTON_TYPES.UP) ||
      (currentIndex === childrenNodes.length - 1 && moveType === MOVE_BUTTON_TYPES.DOWN);
    return isDisabled;
  };

  const isGridColumnElement = component.controlType === SS_CONTROL_TYPE.GRID_COLUMN;

  const isUpButtonDisabled = isMoveBtnDisabled(component, MOVE_BUTTON_TYPES.UP);
  const isDownButtonDisabled = isMoveBtnDisabled(component, MOVE_BUTTON_TYPES.DOWN);

  const tooltipPlusButtonText = getAddButtonTooltipText(component.controlType, translateString);
  const tooltipDeleteButtonText = translateString(`Delete the selected ${component.controlType}`);

  const addControl = (e) => {
    e.stopPropagation();

    switch (component.controlType) {
      case SS_CONTROL_TYPE.GRID:
        dispatch(addNewSubGridToFormModel(component));
        break;
      case SS_CONTROL_TYPE.GRID_COLUMN:
        dispatch(addNewColumnToFormModel(component));
        break;
      case SS_CONTROL_TYPE.SINGLE_TAB:
        dispatch(addNewSingleTabToFormModel(component));
        break;
      case SS_CONTROL_TYPE.TEXT_BOX:
        showTextBoxOptions(e);
        break;
      default:
    }
  };

  const isRotate = getRotate(component);
  const isSSIcon = component?.controlType === SS_CONTROL_TYPE.ICON;

  return (
    <div className={classes.root}>
      {isSelected && hasControlWrapper && (
        <div className={classes.dragIcon} onMouseDown={() => setDragIconPressed(true)}>
          <IconButton className="MuiIconButton" size="small" disabled aria-label="Icon Button">
            <Drag />
          </IconButton>
        </div>
      )}
      <div
        className={classNames({
          [classes.element]: true,
          [classes.elementSelected]: isSelected,
          [classes.iconWrapper]: isSSIcon,
        })}
      >
        {children}
      </div>
      {isSelected && hasControlWrapper && (
        <div
          className={classNames(
            isGridColumnElement ? classes.floatingButtonsBottom : classes.floatingButtons,
            isSSIcon && classes.floatingButtonsIcon
          )}
        >
          <IconButton
            className={classNames('MuiIconButton', {
              [classes.rotate]: isRotate,
              [classes.disabled]: isUpButtonDisabled,
            })}
            ui-ta={getUITA('CtrlOption Up Button')}
            size="small"
            disabled={isUpButtonDisabled}
            onClick={(e) => {
              e.stopPropagation();
              dispatch(changeElementPositionFormModel(component, MOVE_BUTTON_TYPES.UP));
            }}
            aria-label="Up Button"
          >
            <ArrowUp color="primary" />
          </IconButton>
          <IconButton
            className={classNames('MuiIconButton', {
              [classes.rotate]: isRotate,
              [classes.disabled]: isDownButtonDisabled,
            })}
            ui-ta={getUITA('CtrlOption Down Button')}
            size="small"
            disabled={isDownButtonDisabled}
            onClick={(e) => {
              e.stopPropagation();
              dispatch(changeElementPositionFormModel(component, MOVE_BUTTON_TYPES.DOWN));
            }}
            aria-label="Down Button"
          >
            <ArrowDown color="primary" />
          </IconButton>
          <Tooltip title={tooltipPlusButtonText} placement="bottom">
            <IconButton
              className={classNames('MuiIconButton', {
                [classes.disabled]: isAddBtnDisabled,
              })}
              size="small"
              disabled={isAddBtnDisabled}
              onClick={addControl}
              ui-ta={getUITA('CtrlOption AddBtn')}
              aria-label="Add Button"
            >
              <Plus color="primary" />
            </IconButton>
          </Tooltip>
          <Tooltip title={tooltipDeleteButtonText} placement="bottom">
            <IconButton
              ui-ta={getUITA('CtrlOption DeleteBtn')}
              className={classNames('MuiIconButton', {
                [classes.disabled]: isDeleteBtnDisabled,
              })}
              size="small"
              disabled={isDeleteBtnDisabled}
              onClick={(e) => {
                e.stopPropagation();
                dispatch(deleteElementFromFormModel(component));
              }}
              aria-label="Delete Button"
            >
              <Trash color="primary" />
            </IconButton>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default React.memo(NewComponentOptions);
